// src/components/EmployeeNavbar.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { FiHome, FiUser, FiLogOut, FiMenu, FiX, FiMessageSquare, FiSettings, FiCalendar, FiDollarSign } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../assets/Image/Effitrack.png';
import LanguageSelector from '../common/LanguageSelector';

const EmployeeNavbar = ({ employeeData, businessId }) => {
  const { logout } = useAuth();
  const { t } = useTranslation("employee");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Listen for unread messages
  useEffect(() => {
    if (!businessId || !employeeData?.uid) return;

    const messagesRef = collection(db, `businesses/${businessId}/messages`);
    const q = query(
      messagesRef,
      where('participants', 'array-contains', employeeData.uid),
      where('read', '==', false)
    );

    const unsub = onSnapshot(q, (snap) => {
      // Count messages from admin (not from this employee)
      const unread = snap.docs.filter(doc => doc.data().sender !== employeeData.uid).length;
      setUnreadCount(unread);
    });

    return () => unsub();
  }, [businessId, employeeData]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur border-b border-slate-200 shadow-sm z-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <Link to="/employee/dashboard" className="flex items-center gap-3 group">
            <div className="h-10 w-10 rounded-2xl border border-emerald-200 bg-white shadow-sm flex items-center justify-center">
              <img src={Logo} alt="Effi Track" className="relative h-full w-full object-contain" />
            </div>
            <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-sky-600 to-blue-600 group-hover:brightness-110 transition">
              EffiTrack
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/employee/dashboard"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition border border-transparent hover:border-emerald-200"
            >
              <FiHome className="w-5 h-5" />
              <span className="font-medium">{t("nav.dashboard")}</span>
            </Link>

            <Link
              to="/employee/messages"
              className="relative flex items-center gap-2 px-3 py-2 rounded-xl text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition border border-transparent hover:border-emerald-200"
            >
              <FiMessageSquare className="w-5 h-5" />
              <span className="font-medium">{t("nav.messages")}</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            <Link
              to="/employee/calendar"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition border border-transparent hover:border-emerald-200"
            >
              <FiCalendar className="w-5 h-5" />
              <span className="font-medium">{t("nav.calendar")}</span>
            </Link>

            <Link
              to="/employee/profile"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition border border-transparent hover:border-emerald-200"
            >
              <FiSettings className="w-5 h-5" />
              <span className="font-medium">{t("nav.profile")}</span>
            </Link>
            <Link
              to="/employee/tasks"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition border border-transparent hover:border-emerald-200"
            >
              <FiCalendar className="w-5 h-5" />
              <span className="font-medium">{t("nav.tasks")}</span>
            </Link>

            <Link
              to="/employee/expenses"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition border border-transparent hover:border-emerald-200"
            >
              <FiDollarSign className="w-5 h-5" />
              <span className="font-medium">{t("nav.expenses")}</span>
            </Link>

            {/* Language Selector */}
            <LanguageSelector />

            {/* Employee Info */}
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-xl border border-slate-200 shadow-sm">
              <div className="bg-gradient-to-br from-emerald-500 via-sky-500 to-blue-500 p-2 rounded-lg text-white">
                <FiUser className="w-4 h-4" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-900">
                  {employeeData?.name || t("nav.employee")}
                </p>
                <p className="text-xs text-slate-500">
                  {employeeData?.role || t("nav.employee")}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-sky-500 to-blue-500 text-white font-semibold shadow-sm hover:-translate-y-0.5 transition border border-transparent"
            >
              <FiLogOut className="w-4 h-4" />
              <span>{t("nav.logout")}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg border border-slate-200 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 transition"
          >
            {mobileMenuOpen ? (
              <FiX className="w-6 h-6" />
            ) : (
              <FiMenu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white shadow-lg">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/employee/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-emerald-50 rounded-xl transition border border-transparent hover:border-emerald-100"
            >
              <FiHome className="w-5 h-5" />
              <span className="font-medium">{t("nav.dashboard")}</span>
            </Link>

            <Link
              to="/employee/messages"
              onClick={() => setMobileMenuOpen(false)}
              className="relative flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-emerald-50 rounded-xl transition border border-transparent hover:border-emerald-100"
            >
              <FiMessageSquare className="w-5 h-5" />
              <span className="font-medium">{t("nav.messages")}</span>
              {unreadCount > 0 && (
                <span className="ml-auto min-w-[20px] h-[20px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            <Link
              to="/employee/calendar"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-emerald-50 rounded-xl transition border border-transparent hover:border-emerald-100"
            >
              <FiCalendar className="w-5 h-5" />
              <span className="font-medium">{t("nav.calendar")}</span>
            </Link>

            <Link
              to="/employee/profile"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-emerald-50 rounded-xl transition border border-transparent hover:border-emerald-100"
            >
              <FiSettings className="w-5 h-5" />
              <span className="font-medium">{t("nav.profile")}</span>
            </Link>

            <Link
              to="/employee/expenses"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-emerald-50 rounded-xl transition border border-transparent hover:border-emerald-100"
            >
              <FiDollarSign className="w-5 h-5" />
              <span className="font-medium">{t("nav.expenses")}</span>
            </Link>

            {/* Language Selector Mobile */}
            <div className="px-4 py-2">
              <LanguageSelector />
            </div>

            {/* Employee Info Mobile */}
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200 shadow-sm">
              <div className="bg-gradient-to-br from-emerald-500 via-sky-500 to-blue-500 p-2 rounded-lg text-white">
                <FiUser className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {employeeData?.name || t("nav.employee")}
                </p>
                <p className="text-xs text-slate-500">
                  {employeeData?.role || t("nav.employee")}
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 via-sky-500 to-blue-500 text-white font-semibold shadow-sm hover:-translate-y-0.5 transition border border-transparent"
            >
              <FiLogOut className="w-5 h-5" />
              <span>{t("nav.logout")}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default EmployeeNavbar;
